import { CambiarPinDialog } from '@/components/CambiarPinDialog';
import { Button } from '@/components/ui/button';
import { cerrarSesion } from '@/lib/auth';
import { obtenerUsuarioActual } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import { ArrowsLeftRight, CaretDown, Key, List, SignOut } from '@phosphor-icons/react';
import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { IndicadorConexion } from './IndicadorConexion';
import { LogoAdelante } from './LogoAdelante';

const linkBase = 'text-sm transition-colors';
const linkInactive = 'text-zinc-400 hover:text-white';
const linkActive = 'text-white';

interface Props {
  /** Variante visual del header.
   * - 'admin' (default): nav horizontal en el centro (Obras / Configuración / ...).
   * - 'campo': compacto, sin nav horizontal (la nav vive en BottomNavCampo).
   */
  variant?: 'admin' | 'campo';
}

interface ItemNav {
  to: string;
  label: string;
}

// Operativo del día a día — links directos arriba.
const NAV_PRINCIPAL: ItemNav[] = [
  { to: '/admin/obras', label: 'Obras' },
  { to: '/admin/cierre', label: 'Cierre' },
  { to: '/admin/reportes', label: 'Reportes' },
  { to: '/admin/analisis-mensual', label: 'Mensual' },
  { to: '/admin/fotos', label: 'Fotos' },
];

// Catálogos / parámetros — se configuran poco; agrupados en "Configuración ▾".
const NAV_CONFIG: ItemNav[] = [
  { to: '/admin/presupuestos', label: 'Presupuestos' },
  { to: '/admin/sub-partidas', label: 'Sub-partidas' },
  { to: '/admin/tipos-casa', label: 'Tipos de casa' },
  { to: '/admin/sprints', label: 'Sprints' },
  { to: '/admin/pesos', label: 'Pesos' },
  { to: '/admin/causas', label: 'Causas' },
  { to: '/admin/calidad-fichas', label: 'Fichas de control' },
  { to: '/admin/centros-costo-fotos', label: 'Centros de costo (fotos)' },
  { to: '/admin/integracion-bc', label: 'Integración BC' },
];

// Mano de Obra — sección propia (nómina, horas, subcontratos).
const NAV_MO: ItemNav[] = [
  { to: '/admin/mo-nomina', label: 'Nómina Semanal' },
  { to: '/admin/mo-horas', label: 'Horas por Obra' },
  { to: '/admin/mo-subcontratos', label: 'Subcontratos' },
];

// Administración del sistema — al final.
const NAV_FINAL: ItemNav[] = [{ to: '/admin/usuarios', label: 'Usuarios' }];

export function HeaderAdelante({ variant = 'admin' }: Props) {
  const usuario = obtenerUsuarioActual();
  const iniciales = usuario?.iniciales ?? '?';
  const nombreCompleto = usuario?.name ?? 'Sin sesión';
  const usuarioEsAdmin = usuario?.rol === 'admin';
  const usuarioEsGestion = usuario?.rol === 'admin' || usuario?.rol === 'supervisor';
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [configAbierto, setConfigAbierto] = useState(false);
  const [moAbierto, setMoAbierto] = useState(false);
  const [pinAbierto, setPinAbierto] = useState(false);
  const [mobileAbierto, setMobileAbierto] = useState(false);

  // "Configuración" / "M.O." se ven activos si la ruta actual es uno de sus ítems.
  const configActivo = NAV_CONFIG.some((i) => location.pathname.startsWith(i.to));
  const moActivo = NAV_MO.some((i) => location.pathname.startsWith(i.to));

  function handleLogout() {
    cerrarSesion();
    navigate('/login', { replace: true });
  }

  /** Permite al admin saltar entre /admin y /campo desde el menú del avatar. */
  function handleSwitchArea() {
    setMenuAbierto(false);
    navigate(variant === 'admin' ? '/campo' : '/admin');
  }

  const containerCls =
    variant === 'admin'
      ? 'container mx-auto flex h-14 max-w-7xl items-center justify-between px-6'
      : 'flex h-12 items-center justify-between px-4';

  return (
    <header className="sticky top-0 z-40 bg-[#0A0A0A] text-white print:hidden">
      <div className={containerCls}>
        <div className="flex items-center gap-2 md:gap-3">
          {variant === 'admin' && (
            <button
              type="button"
              onClick={() => setMobileAbierto((v) => !v)}
              className="-ml-1 rounded p-1 text-zinc-300 hover:bg-white/10 hover:text-white lg:hidden"
              aria-label="Menú"
            >
              <List className="h-5 w-5" weight="bold" aria-hidden="true" />
            </button>
          )}
          <LogoAdelante altura={variant === 'campo' ? 18 : 22} />
          <span
            className={cn('font-bold tracking-wider', variant === 'campo' ? 'text-xs' : 'text-sm')}
          >
            ADELANTE.
          </span>
        </div>

        {variant === 'admin' && (
          <nav className="hidden items-center gap-6 lg:flex">
            {NAV_PRINCIPAL.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkInactive)}
              >
                {item.label}
              </NavLink>
            ))}

            {/* Configuración: catálogos (poco frecuentes) — solo admin. */}
            {usuarioEsAdmin && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setConfigAbierto((v) => !v)}
                  onBlur={() => setTimeout(() => setConfigAbierto(false), 150)}
                  className={cn(
                    linkBase,
                    'flex items-center gap-1',
                    configActivo ? linkActive : linkInactive,
                  )}
                >
                  Configuración
                  <CaretDown
                    className={cn('h-3 w-3 transition-transform', configAbierto && 'rotate-180')}
                    weight="bold"
                    aria-hidden="true"
                  />
                </button>
                {configAbierto && (
                  <div className="absolute left-0 top-7 z-50 w-48 rounded-md border border-border bg-white p-1 text-foreground shadow-lg">
                    {NAV_CONFIG.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setConfigAbierto(false)}
                        className={({ isActive }) =>
                          cn(
                            'block rounded px-3 py-1.5 text-sm transition-colors',
                            isActive
                              ? 'bg-zinc-100 font-medium text-foreground'
                              : 'text-foreground/80 hover:bg-zinc-50',
                          )
                        }
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Usuarios — solo admin. */}
            {usuarioEsAdmin &&
              NAV_FINAL.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkInactive)}
                >
                  {item.label}
                </NavLink>
              ))}

            {/* Mano de Obra: sección propia (nómina, horas, subcontratos). */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setMoAbierto((v) => !v)}
                onBlur={() => setTimeout(() => setMoAbierto(false), 150)}
                className={cn(
                  linkBase,
                  'flex items-center gap-1',
                  moActivo ? linkActive : linkInactive,
                )}
              >
                M.O.
                <CaretDown
                  className={cn('h-3 w-3 transition-transform', moAbierto && 'rotate-180')}
                  weight="bold"
                  aria-hidden="true"
                />
              </button>
              {moAbierto && (
                <div className="absolute left-0 top-7 z-50 w-48 rounded-md border border-border bg-white p-1 text-foreground shadow-lg">
                  {NAV_MO.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMoAbierto(false)}
                      className={({ isActive }) =>
                        cn(
                          'block rounded px-3 py-1.5 text-sm transition-colors',
                          isActive
                            ? 'bg-zinc-100 font-medium text-foreground'
                            : 'text-foreground/80 hover:bg-zinc-50',
                        )
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>
        )}

        <div className="flex items-center gap-2 md:gap-3">
          {variant === 'admin' && <IndicadorConexion />}

          {/* Cambio rápido de área para admins, sin abrir el menú del avatar. */}
          {usuarioEsGestion && (
            <button
              type="button"
              onClick={() => navigate(variant === 'admin' ? '/campo' : '/admin')}
              className="flex items-center gap-1.5 rounded-md border border-white/20 px-2.5 py-1 text-xs font-medium text-zinc-200 transition-colors hover:bg-white/10 hover:text-white"
              title={`Ir a ${variant === 'admin' ? 'Campo' : 'Admin'}`}
            >
              <ArrowsLeftRight className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
              {variant === 'admin' ? 'Campo' : 'Admin'}
            </button>
          )}

          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuAbierto((v) => !v)}
              onBlur={() => setTimeout(() => setMenuAbierto(false), 150)}
              className={cn(
                'flex items-center justify-center rounded-full bg-adelante-primary text-xs font-bold text-adelante-foreground transition-transform hover:scale-105',
                variant === 'campo' ? 'h-8 w-8' : 'h-7 w-7',
              )}
              aria-label={`Usuario: ${nombreCompleto}`}
              title={nombreCompleto}
            >
              {iniciales}
            </button>

            {menuAbierto && (
              <div className="absolute right-0 top-10 z-50 w-60 rounded-md border border-border bg-white p-1 text-foreground shadow-lg">
                <div className="px-3 py-2 text-sm">
                  <p className="font-medium">{nombreCompleto}</p>
                  <p className="text-xs text-muted-foreground">@{usuario?.username ?? '—'}</p>
                </div>

                {/* En campo el indicador se mueve aquí porque arriba no cabe */}
                {variant === 'campo' && (
                  <div className="border-t border-border px-3 py-2">
                    <IndicadorConexion />
                  </div>
                )}

                <div className="my-1 border-t border-border" />

                {usuarioEsGestion && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-sm"
                    onMouseDown={handleSwitchArea}
                  >
                    <ArrowsLeftRight className="h-4 w-4" weight="bold" aria-hidden="true" />
                    Ir a {variant === 'admin' ? 'Campo' : 'Admin'}
                  </Button>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-2 text-sm"
                  onMouseDown={() => {
                    setMenuAbierto(false);
                    setPinAbierto(true);
                  }}
                >
                  <Key className="h-4 w-4" weight="bold" aria-hidden="true" />
                  Cambiar mi PIN
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-2 text-sm"
                  onMouseDown={handleLogout}
                >
                  <SignOut className="h-4 w-4" weight="bold" aria-hidden="true" />
                  Cerrar sesión
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Menú móvil (admin): la nav horizontal no entra en pantallas chicas. */}
      {variant === 'admin' && mobileAbierto && (
        <nav className="border-white/10 border-t px-4 pb-3 lg:hidden">
          <div className="flex flex-col py-1">
            {[
              ...NAV_PRINCIPAL,
              ...(usuarioEsAdmin ? [...NAV_CONFIG, ...NAV_FINAL] : []),
              ...NAV_MO,
            ].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileAbierto(false)}
                className={({ isActive }) =>
                  cn(
                    'rounded px-3 py-2 text-sm',
                    isActive ? 'bg-white/10 text-white' : 'text-zinc-300 hover:bg-white/5',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            {usuarioEsGestion && (
              <button
                type="button"
                onClick={() => {
                  setMobileAbierto(false);
                  navigate('/campo');
                }}
                className="mt-1 flex items-center gap-2 rounded bg-adelante-primary px-3 py-2 text-left font-medium text-adelante-foreground text-sm"
              >
                <ArrowsLeftRight className="h-4 w-4" weight="bold" aria-hidden="true" />
                Ir a Campo
              </button>
            )}
          </div>
        </nav>
      )}
      <CambiarPinDialog open={pinAbierto} onOpenChange={setPinAbierto} />
    </header>
  );
}
